import { createReducer, on } from '@ngrx/store';
import { AccountInfoActions } from '../actions';
import { TransactionFormActions, StandingAuthorizationsActions, ConfirmationPageActions, AgreementActions, StandingAuthorizationsTableActions, AchActions } from '../../create-request/actions';
import { CashieringTransaction, RecurringTransaction } from '../../create-request/models';
import { StandingInstruction } from '../../shared/types/StandingInstruction';
import { TransactionType } from '../../shared/types/Transaction';
import { TransactionAuthorization } from '../../create-request/models/overview/TransactionAuthorization';
import { Agreement } from '../../create-request/models/overview/Agreement';
import { StandingAuthConfirmationPageActions } from '../../standing-authorizations/actions';
import { ToFromAccount } from '../../create-request/models/ach/ToFromAccount';

export const transactionsKey = 'transactions';

interface AccountTransactions {
  cashieringTransactions: CashieringTransaction[];
  standingInstructions: StandingInstructionById;
  loadPopupCallFinished: boolean;
  agreement: Agreement;
  cashieringRecurringTransactions: RecurringTransaction[];
  hasMoveMoneyDataAccess: boolean | undefined;
  isValidRegType: boolean | undefined;
  transactionAuthorizationDetails: TransactionAuthorization;
  loadTransactionsSuccess: boolean;
}

interface StandingInstructionById {
  [cashieringId: string]: StandingInstruction;
}

export interface State extends AccountTransactions { }

export const initialTransactionsState: State = {
  agreement: {
    agreementText: '',
    canDisplayAgreement: false,
    hasAgreementResponseError: false
  },
  cashieringTransactions: [],
  standingInstructions: {},
  cashieringRecurringTransactions: [],
  hasMoveMoneyDataAccess: undefined,
  isValidRegType: undefined,
  transactionAuthorizationDetails: {
    checkAuthorization: [],
    journalAuthorization: [],
    wireAuthorization: [],
    moneylinkAuthorization: [],
    sloaAuthorization: []
  },
  loadPopupCallFinished: false,
  loadTransactionsSuccess: false
}

export const transactionsReducer = createReducer(
  initialTransactionsState,
  on(
    AccountInfoActions.loadTransactions,
    ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
    () => { return initialTransactionsState }
  ),
  on(AccountInfoActions.updateSubAccount, (state) => {
    return {
      ...state
    }
  }),
  on(AccountInfoActions.loadTransactionsSuccess, (state, response) => {
    return {
      ...state,
      agreement: {
        agreementText: response.agreement?.agreementText,
        canDisplayAgreement: response.agreement?.canDisplayAgreement,
        hasAgreementResponseError: response.agreement?.canDisplayAgreement && response.agreement?.agreementText?.length === 0? true : false
      },
      cashieringTransactions: response.cashieringTransactions,
      standingInstructions: {
        ...updateStandingInstruction(state.standingInstructions, response.mappedInstructions)
      },
      hasMoveMoneyDataAccess: response.hasMoveMoneyDataAccess,
      isValidRegType: response.isValidRegType,
      cashieringRecurringTransactions: response.cashieringRecurringTransactions,
      transactionAuthorizationDetails: response.transactionAuthorizationDetails,
      loadTransactionsSuccess: true
    }
  }),
  on(
    AgreementActions.submitFirstTimeUserAgreementSuccess,
    AgreementActions.submitFirstTimeUserAgreementFailure,
    (state) => {
      return {
        ...state,
        agreement: {
          canDisplayAgreement: false,
          agreementText: '',
          hasAgreementResponseError: false
        }
      }
    }
  ),
  on(AccountInfoActions.loadTransactionsFailure, (state) => {
    return {
      ...state,
      agreement: {
        agreementText: state.agreement.agreementText,
        canDisplayAgreement: state.agreement.canDisplayAgreement,
        hasAgreementResponseError: true
      }
    }
  }),

  on(StandingAuthorizationsActions.loadSloasSuccess, (state, response) => {
    return {
      ...state,
      standingInstructions: {
        ...updateStandingAuthorizationByType(
          state.standingInstructions,
          response.instructions,
          response.transactionType
        )
      },
      loadPopupCallFinished: true
    }
  }),
  on(StandingAuthorizationsActions.loadSloasFailure, (state, err) => {
    return {
      ...state,
      loadPopupCallFinished: true
      //TODO some sort of error setting
    }
  }),
  on(StandingAuthorizationsActions.loadSloas, TransactionFormActions.transactionTypeChanged, (state) => {
    return {
      ...state,
      loadPopupCallFinished: false
    }
  }),
  on(StandingAuthorizationsTableActions.addUpdateDeleteNicknameSuccess, (state, response) => {
    const cashieringReqId = response.nicknameRecord.cashieringRequestId;
    const cashieringReqSecId = response.nicknameRecord.cashieringSecRequestId;
    const transactionType = getTransactionType(response.nicknameRecord.type);
    const uniqueId = `${transactionType}-${cashieringReqId}-${cashieringReqSecId}`;
    if (!!state.standingInstructions[uniqueId]) {
      return {
        ...state,
        standingInstructions: {
          ...state.standingInstructions,
          [uniqueId]: {
            ...state.standingInstructions[uniqueId],
            nickname: response.nicknameRecord.nickname
          }
        }
      }
    } else {
      return {
        ...state
      }
    }
  }),
  on(AchActions.inputCallSuccessful, (state, response) => {
    return {
      ...state,
      standingInstructions: {
        ...updateStandingInstructionAfterInput(
          response.achInfo.toFromAccount,
          state.standingInstructions,
          response.achInfo.cashieringRequestId.toString()
        )
      }
    }
  }),
);

export function getTransactionType(type: string): string {
  var transactionType = type?.toLowerCase();
  if (transactionType === 'j') {
    return 'i';
  }
  return transactionType;
}

/**
 * @description Function to be used with Statusview Experience API Overview calls.
 *  - Updates values for standing authorizations
 *  - Removes instructions which have become inactive
 * @param toBeUpdated
 * @param updates
 * @returns Standing instructions by ID with updates
 */
export function updateStandingInstruction(toBeUpdated: StandingInstructionById, updates: StandingInstruction[]): StandingInstructionById {
  const byId: StandingInstructionById = {};

  updates.forEach((update) => {
    const uniqueKey = `${update.transactionType[0]}-${update.cashieringRqstId}-${update.cashieringRqstSecondId}`
    byId[uniqueKey] = { ...toBeUpdated[uniqueKey], ...update };
  });

  return byId;
}

/**
 * @description Function to be used with transaction-specific mm Exp Standing AUthorizations calls.
 *  - Updates values for standing authorizations
 * @param toBeUpdated
 * @param updates
 * @param type
 * @returns Standing instructions by ID with updates for a specific transaction type
 */
export function updateStandingAuthorizationByType(toBeUpdated: StandingInstructionById, updates: StandingInstruction[], type: TransactionType): StandingInstructionById {
  const byId: StandingInstructionById = {};

  Object.entries(toBeUpdated).forEach(([key, instruction]) => {
    const uniqueKey = `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`;
    if (instruction.transactionType === type) {
      let updatedSloa = updates.find(sloa => sloa.cashieringRqstId === instruction.cashieringRqstId);
      byId[uniqueKey] =
        (updatedSloa !== undefined)
          ? { ...instruction, ...updatedSloa }
          : { ...instruction };
    }
    else {
      byId[uniqueKey] = { ...instruction };
    }
  });

  return byId;
}

export function updateStandingInstructionAfterInput(toFromAccount: ToFromAccount, instructions: StandingInstructionById, cashieringId: string): StandingInstructionById {
  const byId: StandingInstructionById = {};

  Object.entries(instructions).forEach((instruction) => {
    const uniqueKey = instruction[0];

    if (uniqueKey.includes(cashieringId)) {
      byId[uniqueKey] = {
        ...instruction[1],
        accountName: toFromAccount.accountName,
        abaNumber: toFromAccount.abaNumber,
        accountType: toFromAccount.accountType
      }
    }
    else {
      byId[uniqueKey] = { ...instruction[1] };
    }
  });
  return byId;
}
